import { fullRecord } from '../sim/seasonRecord';
import type { ComplianceState } from '../sim/compliance';
import Dexie, { Table } from 'dexie';
import type { Difficulty, LeagueStructure } from '../sim/league';
import type { TalentId } from '../sim/coachPoints';
import { SAVE_FORMAT_GZIP, canCompressSaves, compressText, decompressText } from './saveCodec';
import type { PlayoffBracketState } from '../sim/playoffEngine';
import type { RecentTalks } from '../sim/talkPicker';
import { Team, NarrativeDilemma, FeederProspect, CompactBoxScore, ScheduledGame, DilemmaRecord } from '../types/game';

export interface GameSaveRecord {
  id: string; // 'current_save' or custom profile ID
  saveName: string;
  timestamp: number;
  currentWeek: number;
  userTeamId: string;
  coachPoints?: number; // Coach Points (older saves: coachingAP)
  coachingAP?: number; // legacy action points
  coachTalents?: TalentId[];
  practiceIntensity: string; // sim/training PracticeIntensity (older saves: WALKTHROUGH / STANDARD / CONTACT)
  secondStringLead?: number | null; // the coach's bench line (null: never); older saves: 35
  recentTalks?: RecentTalks; // last game's speech offers and each speech's last line (older saves: none)
  districtTeams: Team[];
  activeDilemma: NarrativeDilemma | null;
  scoutingPool: FeederProspect[];
  history: CompactBoxScore[];
  // Added with the season schedule / league world; older saves may not have them
  currentYear?: number;
  neighborDistrictTeams?: Team[]; // pre-league saves only
  league?: LeagueStructure;
  leagueTeams?: Team[];
  playoffBracket?: PlayoffBracketState | null;
  sanctionLevel?: 0 | 1 | 2 | 3;
  statewideRecruits?: FeederProspect[];
  widePool?: FeederProspect[]; // the State and National prospect lists
  weekResults?: import('../sim/runAhead').WeekResults | null; // this week's games already simulated ahead
  interstateGames?: ScheduledGame[]; // the season's out-of-state games
  coachingStaff?: import('../sim/coachingStaff').HiredCoach[]; // the coach's paid assistants
  career?: import('../sim/careerScore').Career | null; // a scenario career (scored each season)
  careerHistory?: import('../sim/careerScore').Career[]; // the coach's finished contracts in this save (Team › Career)
  userProbationUntil?: number | null; // last season of the coach's recruiting probation
  lightCalibration?: import('../sim/nationalWorld').LightCalibration | null; // the other states' rating mapping
  userViolationHeat?: number; // older saves (booster evidence now lives in compliance)
  compliance?: ComplianceState;
  pendingUserBan?: boolean;
  onHotSeat?: boolean; // board warning from last season's review
  seasonSchedule?: ScheduledGame[];
  nationalLeagues?: import('../sim/nationalWorld').LightLeague[]; // the other states' light leagues
  dilemmaLog?: DilemmaRecord[];
  misconductThreads?: import('../types/game').MisconductThread[]; // calls that can still come back (the Billy Martin rule)
  coachBench?: import('../sim/billyMartin').CoachBench; // games and seasons the coach sits out
  rivalryLog?: import('../sim/rivalryEngine').RivalryResult[]; // the coach's rivalry games (series records, Trophy Case)
  coachReckonings?: number; // reckonings in the coach's career so far
  gradesReviewed?: string; // the week the coach last opened Team › Grades
  bookedGradeFixes?: Record<string, import('../types/game').GradeFix>; // fixes paid for and not yet used, by player
  pressLog?: { questionId: string; year: number; week: number; phrasing?: number }[];
  difficulty?: Difficulty;
  feederClassYear?: number;
  seasonRecap?: import('../store/gameStore').SeasonRecap | null;
  shortlist?: string[]; // the coach's recruiting list (prospect ids)
  programCooldowns?: Partial<Record<import('../sim/feederEngine').FeederEventType, number>>; // weeks before each program runs again
  practiceSeason?: import('../sim/training').PracticeSeason | null; // what practice has added up to this year
  careerKey?: string; // the playthrough, across its saves (its played games are written down outside the saves)
}

/** Lightweight listing for the load screen (full saves are ~10 MB each). */
export interface SaveSummary {
  id: string;
  saveName: string;
  timestamp: number;
  teamName: string;
  wins: number;
  losses: number;
  currentWeek: number;
  currentYear: number;
  difficulty?: Difficulty;
  isAutosave: boolean;
  careerKey?: string;
}

export const AUTOSAVE_ID = 'current_save';

export function summarizeSave(record: GameSaveRecord): SaveSummary {
  const team = (record.leagueTeams ?? record.districtTeams).find((t) => t.id === record.userTeamId);
  return {
    id: record.id,
    saveName: record.saveName,
    timestamp: record.timestamp,
    teamName: team?.name ?? 'Unknown team',
    wins: team ? fullRecord(team, record.playoffBracket).wins : 0,
    losses: team ? fullRecord(team, record.playoffBracket).losses : 0,
    currentWeek: record.currentWeek,
    currentYear: record.currentYear ?? 2026,
    ...(record.difficulty && { difficulty: record.difficulty }),
    ...(record.careerKey && { careerKey: record.careerKey }),
    isAutosave: record.id === AUTOSAVE_ID
  };
}

/** A save as stored: the full record gzip-compressed, with the indexed fields kept alongside. */
interface CompressedSaveRow {
  id: string;
  saveName: string;
  timestamp: number;
  currentWeek: number;
  userTeamId: string;
  format: typeof SAVE_FORMAT_GZIP;
  payload: Uint8Array;
}

/** Older saves are plain records; newer ones are compressed. */
type SaveRow = GameSaveRecord | CompressedSaveRow;

const isCompressed = (row: SaveRow): row is CompressedSaveRow => 'format' in row && row.format === SAVE_FORMAT_GZIP;

export class GameDatabase extends Dexie {
  public saves!: Table<SaveRow, string>;
  public summaries!: Table<SaveSummary, string>;

  public constructor() {
    super('HSFootballHeadCoachDB');
    this.version(1).stores({
      saves: 'id, timestamp, currentWeek, userTeamId'
    });
    // v2: save slots get a summary row so the load screen doesn't read every full save
    this.version(2)
      .stores({
        saves: 'id, timestamp, currentWeek, userTeamId',
        summaries: 'id, timestamp'
      })
      .upgrade(async (tx) => {
        const saves = await tx.table<GameSaveRecord, string>('saves').toArray(); // v1 saves are never compressed
        await tx.table<SaveSummary, string>('summaries').bulkPut(saves.map(summarizeSave));
      });
  }
}

export const db = new GameDatabase();

// Saves are written one at a time, in call order, so a slow earlier autosave never lands after a newer one
let writeQueue: Promise<void> = Promise.resolve();

/** Told about every save written on this device (cloud saves upload a copy when the player is signed in). */
type SaveListener = (summary: SaveSummary, payload: Uint8Array) => void;
let saveListener: SaveListener | null = null;
export const setSaveListener = (listener: SaveListener | null) => {
  saveListener = listener;
};

/**
 * Saves the game to local IndexedDB, gzip-compressed when the browser supports it (~2 MB instead of ~23 MB).
 * The record is snapshotted immediately, since the game keeps mutating its objects after this call.
 */
export function persistSaveGame(record: GameSaveRecord, options: { fromCloud?: boolean } = {}): Promise<void> {
  const json = JSON.stringify(record);
  const summary = summarizeSave(record);
  const write = async () => {
    const row: SaveRow = canCompressSaves()
      ? {
          id: record.id,
          saveName: record.saveName,
          timestamp: record.timestamp,
          currentWeek: record.currentWeek,
          userTeamId: record.userTeamId,
          format: SAVE_FORMAT_GZIP,
          payload: await compressText(json)
        }
      : (JSON.parse(json) as GameSaveRecord);
    await db.transaction('rw', db.saves, db.summaries, async () => {
      await db.saves.put(row);
      await db.summaries.put(summary);
    });
    // A copy downloaded from the cloud is already there
    if (!options.fromCloud && isCompressed(row)) saveListener?.(summary, row.payload);
  };
  const result = writeQueue.then(write);
  writeQueue = result.catch(() => undefined);
  return result;
}

/** All save slots, newest first. */
export async function listSaveSummaries(): Promise<SaveSummary[]> {
  return db.summaries.orderBy('timestamp').reverse().toArray();
}

export async function deleteSaveGame(id: string): Promise<void> {
  await db.transaction('rw', db.saves, db.summaries, async () => {
    await db.saves.delete(id);
    await db.summaries.delete(id);
  });
}

/**
 * Loads a save game record by ID.
 */
export async function loadSaveGame(id = 'current_save'): Promise<GameSaveRecord | undefined> {
  await writeQueue; // include any save still being written
  const row = await db.saves.get(id);
  if (!row) return undefined;
  return isCompressed(row) ? (JSON.parse(await decompressText(row.payload)) as GameSaveRecord) : row;
}

/** A stored save as its summary and gzip bytes (for uploading), or null. */
export async function readSaveForUpload(id: string): Promise<{ summary: SaveSummary; payload: Uint8Array } | null> {
  await writeQueue;
  const [row, summary] = await Promise.all([db.saves.get(id), db.summaries.get(id)]);
  if (!row || !summary || !canCompressSaves()) return null;
  return { summary, payload: isCompressed(row) ? row.payload : await compressText(JSON.stringify(row)) };
}

/** A save downloaded from the cloud (gzip bytes) as a record. */
export async function decodeCloudSave(bytes: Uint8Array): Promise<GameSaveRecord> {
  return JSON.parse(await decompressText(bytes)) as GameSaveRecord;
}

/**
 * Checks if a saved game exists in the browser.
 */
export async function hasSavedGame(id = 'current_save'): Promise<boolean> {
  const count = await db.saves.where('id').equals(id).count();
  return count > 0;
}
